import firebase from 'firebase/app';
import 'firebase/remote-config';
import { logError } from './util/logging';
import emitter from './util/emitter';
import config from './config';

let configPromise = null;
let configActivated = false;
let configError = null;
let firebaseConfig = null;

const MAX_RETRIES = 20;

async function initFirebase() {
  firebase.initializeApp({
    apiKey: config.firebaseApiKey,
    projectId: config.firebaseProjectId,
    appId: config.firebaseAppId,
  });

  firebaseConfig = firebase.remoteConfig();

  firebaseConfig.defaultConfig = {
    dispute_reasons: '[]',
    integrations: {},
    keyboard_shortcuts: '[]',
    review_users: '[]',
  };

  let tries = 0;
  function activate() {
    configError = null;
    configPromise = firebaseConfig.fetchAndActivate();
    configPromise
      .then(() => {
        configActivated = true;
        emitter.emit('remoteConfigActivated');
      })
      .catch(err => {
        configError = true;

        if (navigator.onLine !== false) tries++;

        if (tries >= MAX_RETRIES) {
          logError('Remote Config: Max Retries reached to fetch config', {}, 'warning');
          return;
        }
        logError(
          'Remote Config: Failed to fetch',
          { error: err, time: new Date().toISOString(), try: tries },
          'warning',
        );

        const minutesUntilRetry = Math.min(0.05 * 2 ** tries, 2);
        setTimeout(activate, 60000 * minutesUntilRetry);
      });
  }

  activate();
}

const getConfigPromise = () => configPromise;
const getConfig = () => firebaseConfig;
const isConfigActivated = () => configActivated;
const configHasError = () => configError;

/**
 * Forces an immediate fetch from Firebase, bypassing the minimum fetch interval.
 * Emits 'remoteConfigActivated' when complete so all useRemoteConfig consumers re-render.
 */
async function fetchConfig() {
  if (!firebaseConfig) return;
  const original = firebaseConfig.settings.minimumFetchIntervalMillis;
  firebaseConfig.settings = { minimumFetchIntervalMillis: 0 };
  try {
    await firebaseConfig.fetchAndActivate();
    emitter.emit('remoteConfigActivated');
  } finally {
    firebaseConfig.settings = { minimumFetchIntervalMillis: original };
  }
}

export {
  initFirebase,
  getConfigPromise,
  getConfig,
  isConfigActivated,
  configHasError,
  fetchConfig,
};
